/**
 * Structures and footprint roofs of a Sketch (pure, tested in structures.test.ts).
 *
 * A Sketch can hold several buildings (house, garage, shed, carport), each with its name, eave height and roof.
 * Faces name theirs as `structureId` (`main` for the house), exactly like the automatic pipeline's structures, so
 * layout, quote, BOM, permits and reports total them per building. A structure whose roof was made from a
 * footprint keeps the footprint and each side's mode (hip or gable) and pitch: flipping a side regenerates that
 * structure's roof (footprint.ts, the straight skeleton) without touching the others.
 */
import type { Vec2 } from "../editor/geo.ts";
import type { MessageKey } from "../i18n/keys";
import type { Text } from "./text.ts";
import { gableAllowed, presetSpecs, straightSkeletonRoof, type EdgeSpec, type FootprintPreset } from "./footprint.ts";
import { addLine, centroid, closestOnSegment, nextId, pointInRing, ringArea, type FaceSettings, type SkEdge, type Sketch, type SkStructure, type StructureKind } from "./skeleton.ts";

export const MAIN = "main";
/** The default names a new structure is stored with (the drawing's data); shown translated by `structureLabel`. */
export const STRUCTURE_NAMES: Record<StructureKind, string> = { house: "House", garage: "Garage", shed: "Shed", carport: "Carport", other: "Building" };
const NAME_KEY: Record<StructureKind, MessageKey> = { house: "sketch.structure.house", garage: "sketch.structure.garage", shed: "sketch.structure.shed", carport: "sketch.structure.carport", other: "sketch.structure.building" };

/** A structure's name as the reader sees it: a default name ("House", "Garage 2") in their language, a name the
 * installer typed as typed. */
export function structureLabel(tx: Text, name: string): string {
  for (const kind of Object.keys(STRUCTURE_NAMES) as StructureKind[]) {
    const base = STRUCTURE_NAMES[kind];
    if (name === base) return tx.t(NAME_KEY[kind]);
    const n = name.startsWith(`${base} `) ? name.slice(base.length + 1) : "";
    if (/^\d{1,3}$/.test(n)) return tx.t("sketch.structure.numbered", { name: tx.t(NAME_KEY[kind]), n: Number(n) });
  }
  return name;
}
/** Typical eave heights of new structures by kind. */
export const KIND_EAVE: Record<StructureKind, number> = { house: 3, garage: 2.6, shed: 2.2, carport: 2.4, other: 3 };

const faceStructure = (f: { structureId?: string | null }) => f.structureId || MAIN;

/** The Sketch's structures: the listed ones, plus any a face names (an automatic design's detected garage, a
 * Sketch from before structures), the house first. */
export function structuresOf(s: Sketch): SkStructure[] {
  const out = new Map<string, SkStructure>();
  const listed = s.structures ?? [];
  if (!listed.some((x) => x.id === MAIN)) out.set(MAIN, { id: MAIN, name: "House", kind: "house", eaveHeight: eaveOf(s, MAIN) ?? 3 });
  for (const x of listed) out.set(x.id, x);
  let n = out.size;
  for (const f of s.faces) {
    const id = faceStructure(f);
    if (!out.has(id)) out.set(id, { id, name: `Building ${++n}`, kind: "other", eaveHeight: eaveOf(s, id) ?? 3 });
  }
  return [...out.values()].sort((a, b) => Number(b.id === MAIN) - Number(a.id === MAIN));
}

/** The lowest eave height of a structure's faces. */
function eaveOf(s: Sketch, id: string): number | null {
  const heights = s.faces.filter((f) => faceStructure(f) === id).map((f) => f.eaveHeight);
  return heights.length ? Math.min(...heights) : null;
}

const withStructures = (s: Sketch, list: SkStructure[]): Sketch => ({ ...s, structures: list });

/** A new structure of `kind` (its id and the next free name). */
export function addStructure(s: Sketch, kind: StructureKind): { sketch: Sketch; id: string } {
  const list = structuresOf(s);
  const id = nextId("s", [...list.map((x) => x.id), "s1"]);
  const base = STRUCTURE_NAMES[kind];
  const same = list.filter((x) => x.name === base || x.name.startsWith(`${base} `)).length;
  const structure: SkStructure = { id, name: same ? `${base} ${same + 1}` : base, kind, eaveHeight: KIND_EAVE[kind] };
  return { sketch: withStructures(s, [...list, structure]), id };
}

export function updateStructure(s: Sketch, id: string, change: Partial<Omit<SkStructure, "id">>): Sketch {
  return withStructures(s, structuresOf(s).map((x) => (x.id === id ? { ...x, ...change } : x)));
}

/** The structure's eave height, and every one of its faces with an eave set to it. */
export function setStructureEave(s: Sketch, id: string, height: number): Sketch {
  const h = Math.max(0, Math.round(height * 100) / 100);
  return {
    ...updateStructure(s, id, { eaveHeight: h }),
    faces: s.faces.map((f) => (faceStructure(f) === id && f.eaveHeight !== h ? { ...f, eaveHeight: h } : f)),
  };
}

/** The Sketch without a structure's roof: its faces, the lines no other face uses, their dormers. */
export function clearRoof(s: Sketch, id: string): Sketch {
  const gone = new Set(s.faces.filter((f) => faceStructure(f) === id).map((f) => f.id));
  const faces = s.faces.filter((f) => !gone.has(f.id));
  const used = new Set<string>();
  for (const f of faces) f.boundary.forEach((v, i) => used.add([v, f.boundary[(i + 1) % f.boundary.length]!].sort().join("|")));
  const removedSides = new Set<string>();
  for (const f of s.faces) if (gone.has(f.id)) f.boundary.forEach((v, i) => removedSides.add([v, f.boundary[(i + 1) % f.boundary.length]!].sort().join("|")));
  const edges = s.edges.filter((e) => {
    const key = [e.a, e.b].sort().join("|");
    return !removedSides.has(key) || used.has(key);
  });
  const live = new Set([...edges.flatMap((e) => [e.a, e.b]), ...faces.flatMap((f) => f.boundary)]);
  const vertices = Object.fromEntries(Object.entries(s.vertices).filter(([v]) => live.has(v)));
  const dormers = (s.dormers ?? []).filter((d) => !gone.has(d.faceId));
  // the removed roof split the lines of a building attached to it where it met them: join them again
  const touched = new Set(s.faces.filter((f) => gone.has(f.id)).flatMap((f) => f.boundary));
  return rejoinSplitLines({ ...s, vertices, edges, faces, dormers }, touched, (s.structures ?? []).filter((x) => x.id !== id));
}

/**
 * Joins the two pieces of a line that a removed structure split (a vertex it left on another building's line, now
 * between exactly two collinear lines of the same type): the line is one again, with its first piece's id and
 * direction, and the faces lose the vertex. Corners of a kept footprint stay.
 */
export function rejoinSplitLines(s: Sketch, candidates: Set<string>, structures: SkStructure[] = s.structures ?? []): Sketch {
  const corners = new Set<string>();
  for (const st of structures) for (const p of st.footprint?.ring ?? []) {
    const hit = Object.entries(s.vertices).find(([, v]) => Math.hypot(v[0] - p[0], v[1] - p[1]) < 1e-3);
    if (hit) corners.add(hit[0]);
  }
  let out = s;
  for (const v of candidates) {
    if (!out.vertices[v] || corners.has(v)) continue;
    const at = out.edges.filter((e) => e.a === v || e.b === v);
    if (at.length !== 2 || at[0]!.type !== at[1]!.type) continue;
    const [e1, e2] = [...at].sort((p, q) => idNumber(p.id) - idNumber(q.id)) as [SkEdge, SkEdge];
    const far1 = e1.a === v ? e1.b : e1.a;
    const far2 = e2.a === v ? e2.b : e2.a;
    const p = out.vertices[far1]!, q = out.vertices[far2]!, m = out.vertices[v]!;
    if (closestOnSegment(m, p, q).distance > 1e-3) continue; // a corner, not a split
    if (out.edges.some((e) => (e.a === far1 && e.b === far2) || (e.a === far2 && e.b === far1))) continue;
    const joined: SkEdge = e1.a === v ? { ...e1, a: far2 } : { ...e1, b: far2 };
    const vertices = { ...out.vertices };
    delete vertices[v];
    out = {
      ...out,
      vertices,
      edges: out.edges.filter((e) => e.id !== e2.id).map((e) => (e.id === e1.id ? joined : e)),
      faces: out.faces.map((f) => (f.boundary.includes(v) ? { ...f, boundary: f.boundary.filter((x) => x !== v) } : f)),
    };
  }
  return out;
}

const idNumber = (id: string) => Number(id.replace(/^\D+/, "")) || 0;

/** The Sketch without a structure (its roof and its entry; the house's entry stays, empty). */
export function removeStructure(s: Sketch, id: string): Sketch {
  const cleared = clearRoof(s, id);
  return withStructures(cleared, structuresOf(cleared).filter((x) => x.id !== id || id === MAIN).map((x) => (x.id === id ? { ...x, footprint: null } : x)));
}

/**
 * The structure's roof made from a footprint: the straight skeleton's lines go through `addLine` (so faces, ids and
 * slopes follow the drawing rules) and each new face takes its side's pitch and the structure's eave height.
 */
export function roofFromFootprint(s: Sketch, id: string, ring: Vec2[], edges: EdgeSpec[], defaults: FaceSettings): Sketch {
  const structure = structuresOf(s).find((x) => x.id === id) ?? { id, name: "Building", kind: "other" as const, eaveHeight: defaults.eaveHeight };
  const roof = straightSkeletonRoof([ring], [edges]);
  const settings: FaceSettings = { ...defaults, eaveHeight: structure.eaveHeight, structureId: id };
  let out = s;
  const before = new Set(s.faces.map((f) => f.id));
  // a corner on another building's line (a garage built against the house) splits that line there, so the
  // shared wall is one set of lines both roofs use (never two overlapping lines)
  const snapTo = (sk: Sketch, p: Vec2) => {
    const hit = Object.entries(sk.vertices).find(([, v]) => Math.hypot(v[0] - p[0], v[1] - p[1]) < 1e-3);
    if (hit) return { vertex: hit[0], point: hit[1] };
    const on = sk.edges.find((e) => {
      const c = closestOnSegment(p, sk.vertices[e.a]!, sk.vertices[e.b]!);
      return c.distance < 1e-3 && c.t > 1e-6 && c.t < 1 - 1e-6;
    });
    return on ? { edge: on.id, point: p } : { point: p };
  };
  for (const line of roof.lines) {
    const a: Vec2 = [line.a[0], line.a[1]];
    const b: Vec2 = [line.b[0], line.b[1]];
    out = addLine(out, snapTo(out, a), snapTo(out, b), line.type, settings);
  }
  const faces = out.faces.map((f) => {
    const pts = f.boundary.map((v) => out.vertices[v]!);
    const c = centroid(pts);
    if (!pointInRing(c, ring)) return f;
    const source = roof.faces.find((r) => pointInRing(c, r.points.map((q) => [q[0], q[1]] as Vec2)));
    if (!source || (before.has(f.id) && faceStructure(f) !== id)) return f;
    return { ...f, pitch: source.pitch, eaveHeight: structure.eaveHeight, structureId: id, pitchSource: "drawn" as const };
  });
  // kept counter-clockwise (side i of a reversed ring is side n − 2 − i of the drawn one)
  const n = ring.length;
  const isCcw = ringArea(ring) >= 0;
  const ccw = isCcw ? ring : [...ring].reverse();
  const specs = isCcw ? edges : ccw.map((_, i) => edges[(((n - 2 - i) % n) + n) % n]!);
  return updateStructure({ ...out, faces }, id, { footprint: { ring: ccw.map((p) => [p[0], p[1]] as Vec2), edges: specs.map((e) => ({ mode: e.mode, pitch: e.pitch })) } });
}

/** A preset roof (hip, gable, gable across) on a footprint, for the shape chooser. */
export function presetRoof(s: Sketch, id: string, ring: Vec2[], preset: FootprintPreset, defaults: FaceSettings): Sketch {
  return roofFromFootprint(s, id, ring, presetSpecs(ring, preset, defaults.pitch || 30), defaults);
}

/** The structure's roof made again from its footprint with the sides' modes and pitches given. */
export function regenerate(s: Sketch, id: string, edges: EdgeSpec[], defaults: FaceSettings): Sketch {
  const structure = structuresOf(s).find((x) => x.id === id);
  if (!structure?.footprint) return s;
  return roofFromFootprint(clearRoof(s, id), id, structure.footprint.ring, edges, defaults);
}

/** The footprint side (structure and side index) a drawn line lies on, or null. */
export function footprintSideOf(s: Sketch, a: Vec2, b: Vec2): { structure: SkStructure; side: number } | null {
  for (const structure of structuresOf(s)) {
    const ring = structure.footprint?.ring;
    if (!ring) continue;
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i]!;
      const q = ring[(i + 1) % ring.length]!;
      if (closestOnSegment(a, p, q).distance < 1e-3 && closestOnSegment(b, p, q).distance < 1e-3) return { structure, side: i };
    }
  }
  return null;
}

/** Flip a footprint side gable ↔ hip (a side between two outside corners only) and make the roof again. */
export function toggleSide(s: Sketch, id: string, side: number, defaults: FaceSettings): Sketch {
  const structure = structuresOf(s).find((x) => x.id === id);
  const fp = structure?.footprint;
  if (!fp || !gableAllowed(fp.ring, side)) return s;
  const edges = fp.edges.map((e, i) => (i === side ? { ...e, mode: e.mode === "gable" ? ("hip" as const) : ("gable" as const) } : e));
  if (edges.every((e) => e.mode === "gable")) return s;
  return regenerate(s, id, edges, defaults);
}

/** A footprint side's pitch, and the roof made again. */
export function setSidePitch(s: Sketch, id: string, side: number, pitch: number, defaults: FaceSettings): Sketch {
  const fp = structuresOf(s).find((x) => x.id === id)?.footprint;
  if (!fp) return s;
  return regenerate(s, id, fp.edges.map((e, i) => (i === side ? { ...e, pitch } : e)), defaults);
}

/** The structure a plan point is on (its faces), or null. */
export function structureAt(s: Sketch, p: Vec2): string | null {
  for (const f of s.faces) {
    const pts = f.boundary.map((v) => s.vertices[v]!).filter(Boolean);
    if (pts.length >= 3 && pointInRing(p, pts)) return faceStructure(f);
  }
  return null;
}

/** Structure id → its faces' count and plan area (the list's small line). */
export function structureStats(s: Sketch): Record<string, { faces: number; area: number }> {
  const out: Record<string, { faces: number; area: number }> = {};
  for (const f of s.faces) {
    const id = faceStructure(f);
    const pts = f.boundary.map((v) => s.vertices[v]!).filter(Boolean);
    const item = (out[id] ??= { faces: 0, area: 0 });
    item.faces += 1;
    item.area += Math.abs(ringArea(pts));
  }
  return out;
}

/** Faces of hidden structures (the views leave them out; they are still designed and quoted). */
export function hiddenFaces(s: Sketch): Set<string> {
  const hidden = new Set(structuresOf(s).filter((x) => x.hidden).map((x) => x.id));
  return new Set(s.faces.filter((f) => hidden.has(faceStructure(f))).map((f) => f.id));
}

